import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface KBAccessResult {
  hasFullAccess: boolean;
  accessReason: 'creator' | 'attribution' | 'kb_access' | 'channel_member' | 'admin' | 'none';
}

export const useKBAccess = (kbId: string | undefined, userId: string | undefined) => {
  return useQuery({
    queryKey: ['kb-access', kbId, userId],
    queryFn: async (): Promise<KBAccessResult> => {
      if (!kbId || !userId) {
        return { hasFullAccess: false, accessReason: 'none' };
      }

      // Check if user is admin
      const { data: isAdmin } = await supabase
        .rpc('has_role', { _user_id: userId, _role: 'admin' });
      
      if (isAdmin) {
        return { hasFullAccess: true, accessReason: 'admin' };
      }

      // Check if user is creator
      const { data: kb } = await supabase
        .from('knowledge_bases')
        .select('creator_id, channel_id, status')
        .eq('id', kbId)
        .maybeSingle();

      if (!kb) {
        return { hasFullAccess: false, accessReason: 'none' };
      }

      if (kb.creator_id === userId) {
        return { hasFullAccess: true, accessReason: 'creator' };
      }

      // Check if user has explicit kb_access
      const { data: kbAccess } = await supabase
        .from('kb_access')
        .select('id')
        .eq('knowledge_base_id', kbId)
        .eq('user_id', userId)
        .maybeSingle();

      if (kbAccess) {
        return { hasFullAccess: true, accessReason: 'kb_access' };
      }

      // Check if user is attributed
      const { data: attribution } = await supabase
        .from('attributions')
        .select('id')
        .eq('knowledge_base_id', kbId)
        .eq('user_id', userId)
        .maybeSingle();

      if (attribution) {
        return { hasFullAccess: true, accessReason: 'attribution' };
      }

      // Check if user is in the same channel (for published KBs)
      if (kb.channel_id && kb.status === 'published') {
        const { data: channelMember } = await supabase
          .from('user_organizations')
          .select('id')
          .eq('channel_id', kb.channel_id)
          .eq('user_id', userId)
          .maybeSingle();

        if (channelMember) {
          return { hasFullAccess: true, accessReason: 'channel_member' };
        }
      }

      return { hasFullAccess: false, accessReason: 'none' };
    },
    enabled: !!kbId && !!userId,
    staleTime: 30000,
  });
};
